import { useEffect, useState, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useTranslation } from 'react-i18next';
import Button from 'components/base/Button';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import { fetchRole, updateRole, createRole } from '../../../../redux/slices/rolesSlice';
import { deepEqual } from 'helpers/deepEqual';

import { getZones } from '../../../../redux/slices/lists/zonesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import AssignmentToggleList from '../AssignmentToggleList';
import { haveSameIds } from 'helpers/assignmentUtils';
import PrivilegesCheckboxGroup from '../users/usersPrivileges';
import { getErrorMessage } from 'helpers/getErrorMessage';
import api from 'services/api';
import { notify } from 'messages';

interface AvailableUser {
  user_id: number;
  username: string;
  mail?: string | null;
  assigned?: boolean;
}

const fetchAllUsers = async () => {
  const limit = 200;
  let page = 1;
  const users: any[] = [];

  while (true) {
    const response = await api.get('/users', {
      params: { limit, page },
    });
    const pageUsers = Array.isArray(response.data?.users) ? response.data.users : Array.isArray(response.data) ? response.data : [];

    users.push(...pageUsers);

    const totalPages = Number(response.data?.pagination?.total_pages || 0);
    if (totalPages ? page >= totalPages : pageUsers.length < limit) {
      break;
    }

    page += 1;
  }

  return users;
};

const EditRole = (props: {
  stateId?: number;
  newRole?: boolean;
  asDropdownItem?: boolean;
  forceShow?: boolean;
  onRequestClose?: () => void;
  hideTrigger?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [ssoroleid, setSsoRoleId] = useState<string | null>(null);
  const [ssoroleidTouched, setSsoRoleIdTouched] = useState(false);
  const [privileges, setPrivileges] = useState<any[]>([]);
  const [initialPrivileges, setInitialPrivileges] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [initialUserIds, setInitialUserIds] = useState<number[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoadError, setUsersLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const currentFormValues = useMemo(
    () => ({ name, ssoroleid, privileges, notificationEmail, selectedUserIds }),
    [name, ssoroleid, privileges, notificationEmail, selectedUserIds]
  );
  const isDirtyRaw = useUnsavedChanges(show, currentFormValues);
  const isDirty = !props.newRole && isDirtyRaw;

  const role_id = useAppSelector((state) => (props.newRole || props.stateId === undefined ? undefined : state.roles.roles[props.stateId]?.role_id));

  const fetchedRole = useAppSelector((state) => (props.newRole ? null : state.roles.roles.find((r: any) => r.role_id === role_id)));

  const zones = useAppSelector((state) => state.listOfZones?.zones || []);

  const currentUserId = useAppSelector((state) => state.auth?.userData?.user_id);

  useEffect(() => {
    dispatch(getZones() as any);
  }, [dispatch]);

  useEffect(() => {
    if (props.forceShow !== undefined) {
      setShow(props.forceShow);
    }
  }, [props.forceShow]);

  useEffect(() => {
    // ✅ Guard so we don't call /roles/undefined
    if (show && !props.newRole && role_id != null) {
      dispatch(fetchRole(String(role_id)) as any);
    }
  }, [show, props.newRole, role_id, dispatch]);

  useEffect(() => {
    if (fetchedRole && !props.newRole) {
      setName(fetchedRole.name);
      setPrivileges(fetchedRole.privileges || []);
      setInitialPrivileges(structuredClone(fetchedRole.privileges || []));
      setNotificationEmail(fetchedRole.notification_email || '');
      setSsoRoleId(fetchedRole.ssoroleid ?? null);
      setSsoRoleIdTouched(false);
      setSaveError(null);
    }
  }, [fetchedRole, props.newRole]);

  useEffect(() => {
    const run = async () => {
      setAvailableUsers([]);
      setSelectedUserIds([]);
      setInitialUserIds([]);
      setUsersLoaded(false);
      setUsersLoadError(null);

      if (!show || (!props.newRole && role_id == null)) {
        return;
      }

      try {
        const users = props.newRole ? await fetchAllUsers() : (await api.get(`/roles/${role_id}/users`)).data?.users || [];

        const normalizedUsers: AvailableUser[] = users.map((user: any): AvailableUser => ({
          user_id: user.user_id,
          username: user.username,
          mail: user.mail,
          assigned: Boolean(user.assigned),
        }));
        const assignedUserIds = normalizedUsers.filter((user) => user.assigned).map((user) => user.user_id);

        setAvailableUsers(normalizedUsers);
        setSelectedUserIds(assignedUserIds);
        setInitialUserIds(assignedUserIds);
        setUsersLoaded(true);
      } catch (error: any) {
        console.error('Failed to fetch users:', error);
        setUsersLoadError(getErrorMessage(error?.response?.data, t('errors.failedToLoadUsers')));
      }
    };

    run();
  }, [show, props.newRole, role_id, t]);

  const handleClose = () => {
    setShow(false);
    if (props.onRequestClose) props.onRequestClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  const handleShow = () => {
    setShow(true);
    setSaveError(null);
    // ✅ Guard so we don't call /roles/undefined
    if (!props.newRole && role_id != null) {
      dispatch(fetchRole(String(role_id)) as any);
    }
  };

  const handleSubmitRole = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    setSaveError(null);
    const body: any = { name };

    if (!deepEqual(privileges, initialPrivileges)) {
      body.privileges = privileges;
    }

    if (ssoroleidTouched) {
      body.ssoroleid = ssoroleid;
    }

    const isValidEmail = notificationEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationEmail);

    if (!isValidEmail) {
      alert(t('errors.invalidEmailFormat'));
      return;
    }

    if (isValidEmail) {
      body.notification_email = notificationEmail;
    }

    if (props.newRole && selectedUserIds.length) {
      body.user_ids = selectedUserIds;
    }

    let result;
    if (props.newRole) {
      result = await dispatch(createRole(body) as any);
    } else {
      body.role_id = String(role_id);
      result = await dispatch(updateRole(body) as any);
    }

    if (createRole.fulfilled.match(result) || updateRole.fulfilled.match(result)) {
      if (!props.newRole && role_id != null && usersLoaded && !haveSameIds(selectedUserIds, initialUserIds)) {
        try {
          const usersResponse = await api.put(`/roles/${role_id}/users`, {
            user_ids: selectedUserIds,
          });
          const users = Array.isArray(usersResponse.data?.users) ? usersResponse.data.users : [];
          const normalizedUsers: AvailableUser[] = users.map((user: any): AvailableUser => ({
            user_id: user.user_id,
            username: user.username,
            mail: user.mail,
            assigned: Boolean(user.assigned),
          }));
          const assignedUserIds = normalizedUsers.filter((user) => user.assigned).map((user) => user.user_id);

          setAvailableUsers(normalizedUsers);
          setSelectedUserIds(assignedUserIds);
          setInitialUserIds(assignedUserIds);
        } catch (error: any) {
          setSaveError(getErrorMessage(error?.response?.data, t('errors.failedToSaveRoleUsers')));
          return;
        }
      }

      if (updateRole.fulfilled.match(result)) {
        const messageKey = 'data-update-role';
        if (!notify.isShowing(messageKey)) {
          notify.success(t('roles.updatedSuccessfully'), { key: messageKey });
        }
      } else {
        const messageKey = 'data-create-role';
        if (!notify.isShowing(messageKey)) {
          notify.success(t('roles.createdSuccessfully'), { key: messageKey });
        }
      }
      handleClose();
      return;
    }

    setSaveError(getErrorMessage(result?.payload, t('errors.failedToSaveRole')));
  };

  const togglePrivilege = (privilege_id: number, zone_id: number) => {
    setPrivileges((prev) =>
      prev.map((priv) =>
        priv.privilege_id === privilege_id && priv.zone_id === zone_id
          ? {
              ...priv,
              assigned: !priv.assigned,
            }
          : priv
      )
    );
  };

  const filteredPrivileges = privileges.filter((priv) => priv.name.toLowerCase().includes(filter.toLowerCase()));

  const handleSsoRoleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSsoRoleId(e.target.value);
    setSsoRoleIdTouched(true);
  };

  const handleSelectAllZonePrivileges = (zoneId: number, assign: boolean) => {
    const visiblePrivilegeKeys = new Set(filteredPrivileges.filter((priv) => priv.zone_id === zoneId).map((priv) => `${priv.privilege_id}:${priv.zone_id}`));

    setPrivileges((prev) => prev.map((priv) => (visiblePrivilegeKeys.has(`${priv.privilege_id}:${priv.zone_id}`) ? { ...priv, assigned: assign } : priv)));
  };

  const handleUserToggle = (userId: number) => {
    setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((selectedUserId) => selectedUserId !== userId) : [...prev, userId]));
  };

  const userAssignmentOptions = availableUsers.map((user) => ({
    id: user.user_id,
    label: user.username || user.mail || String(user.user_id),
    detail: user.mail && user.mail !== user.username ? user.mail : null,
  }));
  const assignedUserCount = selectedUserIds.filter((userId) => availableUsers.some((user) => user.user_id === userId)).length;
  const isExistingSsoRole = !props.newRole && Boolean(fetchedRole?.ssoroleid);
  const showSelfAdminGuardNote = Boolean(currentUserId && fetchedRole?.is_protected_admin_role && selectedUserIds.includes(currentUserId));

  return (
    <>
      {!props.hideTrigger ? (
        <>
          {props.asDropdownItem ? (
            <Dropdown.Item onClick={handleShow}>{props.newRole ? t('button.new') : t('button.edit')}</Dropdown.Item>
          ) : (
            <Button variant="phoenix-secondary" onClick={handleShow}>
              {props.newRole ? t('button.new') : t('button.edit')}
            </Button>
          )}
        </>
      ) : null}

      <Modal show={show} onHide={handleRequestClose} backdrop="static" keyboard={false} size="xl">
        <Modal.Header>
          <Modal.Title>
            {props.newRole ? t('button.newe') : t('button.edit')} {t('roles.role')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {saveError ? <Alert variant="danger">{saveError}</Alert> : null}

          <Tabs defaultActiveKey="general" id="edit-role-tabs" className="mb-3" variant="underline">
            <Tab eventKey="general" title={t('general')}>
              <Form.Group as={Row} className="mb-3" controlId="formRoleName">
                <Form.Label column sm="2">
                  {t('name')}
                </Form.Label>
                <Col sm="10">
                  <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="formSSORoleID">
                <Form.Label column sm="2">
                  {t('roles.ssoRoleId')}
                </Form.Label>
                <Col sm="10">
                  <Form.Control type="text" value={ssoroleid || ''} readOnly={isExistingSsoRole} onChange={handleSsoRoleIdChange} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="formNotificationEmail">
                <Form.Label column sm="2">
                  Notification Email
                </Form.Label>
                <Col sm="10">
                  <Form.Control type="email" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} />
                </Col>
              </Form.Group>
            </Tab>

            <Tab eventKey="privileges" title={t('common.privileges')}>
              <Form.Group as={Row} className="mb-3" controlId="formPrivilegeFilter">
                <Form.Label column sm="2">
                  {t('roles.filterPrivileges')}
                </Form.Label>
                <Col sm="10">
                  <Form.Control type="text" placeholder={t('search') + '...'} value={filter} onChange={(e) => setFilter(e.target.value)} />
                </Col>
              </Form.Group>

              {!props.newRole && zones.length > 0 ? (
                <PrivilegesCheckboxGroup
                  privileges={filteredPrivileges}
                  zones={zones}
                  onTogglePrivilege={togglePrivilege}
                  onToggleAllZonePrivileges={handleSelectAllZonePrivileges}
                />
              ) : (
                <p className="text-muted m-3">{t('common.noPrivilegesAvailable')}</p>
              )}
            </Tab>

            <Tab eventKey="users" title={t('roles.users')}>
              {usersLoadError ? (
                <Alert variant="danger">{usersLoadError}</Alert>
              ) : !usersLoaded ? (
                <div className="d-flex justify-content-center align-items-center m-4">
                  <Spinner animation="border" role="status" size="sm" />
                </div>
              ) : (
                <AssignmentToggleList
                  options={userAssignmentOptions}
                  selectedIds={selectedUserIds}
                  onToggle={handleUserToggle}
                  searchPlaceholder={t('roles.searchUsers')}
                  assignedSummary={t('common.assignedSummary', {
                    assigned: assignedUserCount,
                    total: availableUsers.length,
                  })}
                  emptyMessage={t('roles.noUsersFound')}
                  checkboxPrefix="role-user"
                />
              )}
              {showSelfAdminGuardNote ? <Form.Text muted>{t('common.selfAdminGuardNote')}</Form.Text> : null}
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="phoenix-danger" onClick={handleRequestClose} startIcon={<FontAwesomeIcon icon={faWindowClose} />}>
            {t('button.close')}
          </Button>
          <Button variant="phoenix-primary" onClick={handleSubmitRole} startIcon={<FontAwesomeIcon icon={faSave} />}>
            {props.newRole ? t('button.create') : t('button.saveAndClose')}
          </Button>
        </Modal.Footer>
        <ConfirmationModal
          show={showCloseConfirm}
          title="Unsaved Changes"
          body="You have unsaved changes. Are you sure you want to close without saving?"
          confirmText="Discard Changes"
          confirmVariant="danger"
          onConfirm={() => {
            setShowCloseConfirm(false);
            handleClose();
          }}
          onCancel={() => setShowCloseConfirm(false)}
        />
      </Modal>
    </>
  );
};

export default EditRole;
